/**
 * AI CONTEXT: mobile-first - Header component with authentication buttons
 * 
 * This component provides navigation with:
 * - Mobile-first responsive design
 * - Authentication state management
 * - Registration and login buttons for new users
 * - User menu for authenticated users
 * - Touch-friendly interface elements
 * 
 * Architecture decisions:
 * - Show both register and login buttons for unauthenticated users
 * - Use glass morphism design for modern look
 * - Implement proper mobile navigation
 * - Follow established design patterns
 * 
 * Future considerations:
 * - Add user profile picture
 * - Implement notification badges
 * - Add search functionality
 * - Consider social login buttons
 */

/**
 * AI CONTEXT: mobile-first - Header with auth-aware navigation
 * 
 * Purpose:
 * - Present brand/logo and primary navigation
 * - Reflect authentication state (login/register vs user menu)
 * - Maintain responsive, accessible layout
 * 
 * Future considerations:
 * - Add search and quick actions
 * - Sticky promo banner support
 * - Role-based nav customization
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { getAssetUrl } from '../../utils/assetUtils';
import { t } from '../../i18n';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { getCartCount } = useCart();
  const location = useLocation();
  const userMenuRef = useRef(null);

  // AI CONTEXT: ux - Close user menu when clicking outside
  // Reasoning: Provide better user experience with intuitive menu behavior
  // Constraints: Must handle all click events properly
  // Future: Consider implementing keyboard navigation

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.store'), href: '/store' },
    { name: t('nav.giveaways'), href: '/giveaways' },
    { name: t('nav.acquire'), href: '/acquisition' },
    { name: t('nav.blog'), href: '/blog' },
    { name: t('nav.rules'), href: '/legal#no-purchase-necessary' },
    { name: t('nav.dashboard'), href: '/dashboard' },
    ...(user?.isAdmin ? [{ name: t('nav.admin'), href: '/admin' }] : []),
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('/blog')) return location.pathname.startsWith('/blog');
    const base = path.split('#')[0];
    return location.pathname === base;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src={getAssetUrl("/files/Logo.png")} 
              alt="Tolbert Tactical" 
              className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive(item.href)
                    ? 'bg-glass-orange text-white shadow-glow'
                    : 'text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Cart */}
            <Link
              to="/checkout"
              className="relative p-2 text-theme-silver hover:text-theme-bronze transition-all duration-300 hover:bg-glass-white/10 rounded-xl"
            >
              <ShoppingCart className="h-6 w-6" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-theme-bronze text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-glow">
                  {getCartCount()}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button 
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 text-theme-silver hover:text-theme-bronze transition-all duration-300 hover:bg-glass-white/10 rounded-xl"
                >
                  <User className="h-6 w-6" />
              <span className="text-sm font-medium">{user?.name || t('header.user')}</span>
                </button>
                
                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 glass-card-dark py-1 z-50">
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-sm text-theme-silver hover:bg-glass-white/10 transition-all duration-200"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      {t('nav.dashboard')}
                    </Link>
                    {user?.isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2 text-sm text-custom-orange hover:bg-custom-orange/10 transition-all duration-200"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                         {t('nav.admin')}
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-all duration-200"
                    >
                      <div className="flex items-center space-x-2">
                        <LogOut className="h-4 w-4" />
                         <span>{t('auth.signout')}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // AI CONTEXT: ux - Authentication buttons for unauthenticated users
              // Reasoning: Provide clear paths for both new and existing users
              // Constraints: Must be mobile-friendly and accessible
              // Future: Consider adding social login options
              <div className="flex items-center space-x-3">
                <Link
                  to="/register"
                  className="px-4 py-2 rounded-xl text-sm font-medium text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10 transition-all duration-300"
                >
                  {t('auth.signup')}
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-xl text-sm font-medium text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10 transition-all duration-300"
                >
                  {t('auth.signin')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-theme-silver hover:text-theme-bronze transition-all duration-300 hover:bg-glass-white/10 rounded-xl"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 glass-card-dark mt-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-xl text-base font-medium transition-all duration-300 ${
                    isActive(item.href)
                      ? 'bg-glass-orange text-white shadow-glow'
                      : 'text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Cart */}
              <Link
                to="/checkout"
                className="flex items-center justify-between px-3 py-2 rounded-xl text-base font-medium text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10 transition-all duration-300"
                onClick={() => setIsMenuOpen(false)}
              >
                 <span>{t('cart.title')}</span>
                <div className="flex items-center space-x-2">
                  <ShoppingCart className="h-5 w-5" />
                  {getCartCount() > 0 && (
                    <span className="bg-theme-bronze text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-glow">
                      {getCartCount()}
                    </span>
                  )}
                </div>
              </Link>

              {/* Mobile User Actions */}
              {isAuthenticated ? (
                <div className="px-3 py-2">
                  <div className="flex items-center space-x-2 text-theme-silver">
                    <User className="h-5 w-5" />
                     <span className="text-sm font-medium">{user?.name}</span>
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                    className="mt-2 text-sm text-red-400 hover:text-red-300 transition-colors duration-200"
                  >
                    {t('auth.signout')}
                  </button>
                </div>
              ) : (
                // AI CONTEXT: mobile-first - Mobile authentication buttons
                // Reasoning: Provide easy access to registration and login on mobile
                // Constraints: Must fit well in mobile navigation
                // Future: Consider implementing biometric authentication
                <div className="space-y-2">
                  <Link
                    to="/register"
                    className="block px-3 py-2 rounded-xl text-base font-medium text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10 transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('auth.signup')}
                  </Link>
                  <Link
                    to="/login"
                    className="block px-3 py-2 rounded-xl text-base font-medium text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10 transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('auth.signin')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 